import { Globe, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { Market } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteMarketDialog } from '../components/delete-market-dialog';
import { ExchangeRatesCard } from '../components/exchange-rates-card';
import { MarketCard, type MarketAction } from '../components/market-card';
import { MarketDialog } from '../components/market-dialog';
import { useUpdateMarket } from '../hooks/use-market-mutations';
import { useMarkets } from '../hooks/use-markets';
import { marketChangeErrorKey } from '../schemas/market-rules';

type Editing = { open: false } | { open: true; market: Market | null };

function Markets() {
  const { t } = useTranslation('markets');
  const store = useStore();
  const query = useMarkets(store.id);
  const update = useUpdateMarket(store.id);
  const message = useErrorMessage();
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [deleting, setDeleting] = useState<Market | null>(null);
  const markets = query.data ?? [];
  const { editable, reason } = useStoreEditable({ permission: 'settings.write' });

  const patch = (market: Market, body: Parameters<typeof update.mutate>[0]['body']) =>
    update.mutate(
      { id: market.id, body },
      {
        onError: (error) => {
          const key = marketChangeErrorKey(error);
          toast.error(key ? t(key) : message(error));
        },
      },
    );

  const onAction = (action: MarketAction, market: Market) => {
    switch (action) {
      case 'edit':
        setEditing({ open: true, market });
        return;
      case 'makeDefault':
        patch(market, { isDefault: true });
        return;
      case 'activate':
        patch(market, { status: 'active' });
        return;
      case 'deactivate':
        patch(market, { status: 'inactive' });
        return;
      case 'delete':
        setDeleting(market);
    }
  };

  const addButton = (tour?: 'settings.markets.add') =>
    editable ? (
      <Button data-tour={tour} onClick={() => setEditing({ open: true, market: null })}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name, currency: store.defaultCurrency })}
        actions={addButton('settings.markets.add')}
      />
      <ReadOnlyBanner reason={reason} />
      {query.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : markets.length === 0 ? (
        <EmptyState
          icon={Globe}
          title={t('empty.title')}
          description={t('empty.description')}
          action={addButton()}
          tour="settings.markets.empty"
          tourLink
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-tour="settings.markets.card">
          {markets.map((market) => (
            <MarketCard
              key={market.id}
              market={market}
              editable={editable}
              storeCurrency={store.defaultCurrency}
              onAction={onAction}
            />
          ))}
        </div>
      )}
      {editing.open ? (
        <MarketDialog
          key={editing.market?.id ?? 'new'}
          storeId={store.id}
          storeCurrency={store.defaultCurrency}
          markets={markets}
          market={editing.market}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <ExchangeRatesCard />
      <DeleteMarketDialog
        storeId={store.id}
        market={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </PageContent>
  );
}

export function MarketsPage() {
  return (
    <FeatureErrorBoundary name="markets">
      <Markets />
    </FeatureErrorBoundary>
  );
}
